import { useState, useEffect } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import pl from 'date-fns/locale/pl';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { toast } from "sonner";
import axios from "axios";
import { Sparkles, Calendar as CalendarIcon, Facebook, Instagram, Share2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locales = {
    'pl': pl,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

export default function SocialCalendarPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    // Load existing events (mock for now or stored in DB in real app)
    // For this demo, we'll just keep them in state or generate new ones.

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            // Hardcoded "Restaurant" for demo, ideally comes from business profile
            const res = await axios.get(`${API}/social/generate-ideas/restauracja?days=7`, { withCredentials: true });

            const newEvents = res.data.map(idea => ({
                title: idea.title,
                start: new Date(idea.date),
                end: new Date(idea.date),
                allDay: true,
                resource: { content: idea.content }
            }));

            setEvents(prev => [...prev, ...newEvents]);
            toast.success(`Wygenerowano ${newEvents.length} pomysłów na posty!`);
        } catch (error) {
            toast.error("Błąd generowania postów");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
    };

    const handlePublish = async () => {
        if (!selectedEvent) return;

        try {
            await axios.post(`${API}/social/publish`, {
                message: `${selectedEvent.title}\n\n${selectedEvent.resource.content}`
            }, { withCredentials: true });

            toast.success("Post opublikowany (lub zaplanowany)!");
            setSelectedEvent(null);
        } catch (error) {
            toast.error("Błąd publikacji");
        }
    };

    const eventStyleGetter = (event, start, end, isSelected) => {
        return {
            style: {
                backgroundColor: '#F59E0B',
                borderRadius: '5px',
                opacity: 0.8,
                color: 'white',
                border: '0px',
                display: 'block'
            }
        };
    };

    return (
        <div className="h-[calc(100vh-100px)] flex flex-col space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <CalendarIcon className="w-6 h-6 text-blue-600" /> Kalendarz Social Media
                    </h1>
                    <p className="text-slate-500">Planuj i automatyzuj posty na Facebooka i Instagram.</p>
                </div>
                <Button onClick={handleGenerate} disabled={isGenerating} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generuj Pomysły AI
                </Button>
            </div>

            <Card className="flex-1 overflow-hidden shadow-lg border-slate-200">
                <CardContent className="p-4 h-full">
                    <BigCalendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        culture='pl'
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        messages={{
                            next: "Następny",
                            previous: "Poprzedni",
                            today: "Dziś",
                            month: "Miesiąc",
                            week: "Tydzień",
                            day: "Dzień",
                            agenda: "Agenda",
                            date: "Data",
                            time: "Czas",
                            event: "Wydarzenie",
                            noEventsInRange: "Brak postów w tym okresie."
                        }}
                    />
                </CardContent>
            </Card>

            <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Szczegóły Posta</DialogTitle>
                    </DialogHeader>

                    {selectedEvent && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-500">Tytuł</label>
                                <div className="font-semibold text-lg">{selectedEvent.title}</div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-500">Treść (AI)</label>
                                <Textarea
                                    className="min-h-[150px] bg-slate-50 border-slate-200"
                                    defaultValue={selectedEvent.resource.content}
                                    onChange={(e) => selectedEvent.resource.content = e.target.value}
                                />
                            </div>

                            <div className="flex gap-2 justify-end">
                                <Button variant="outline" onClick={() => setSelectedEvent(null)}>Anuluj</Button>
                                <Button onClick={handlePublish} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                                    <Share2 className="w-4 h-4" /> Opublikuj Teraz
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
